/**
 * Event Registration Page for Re:Nav-96
 *
 * Public page for event registration. Allows visitors to register
 * for the 30th year reunion event and receive a ticket.
 */

import { Link } from 'react-router';
import { RegistrationForm } from '@/components/public/RegistrationForm';
import { CountdownTimer } from '@/components/public/CountdownTimer';
import '@/styles/animations.css';

/**
 * Feature highlight for the registration page
 */
interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function Feature({ icon, title, description }: FeatureProps) {
  return (
    <div className="flex items-start gap-4">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
          border: '1px solid rgba(233, 69, 96, 0.2)',
        }}
      >
        {icon}
      </div>
      <div>
        <h4 className="text-white font-medium mb-1">{title}</h4>
        <p className="text-white/60 text-sm">{description}</p>
      </div>
    </div>
  );
}

/**
 * EventRegisterPage Component
 *
 * Provides the public event registration experience with:
 * - Event branding and countdown
 * - Registration form
 * - Event highlights and benefits
 *
 * @example
 * ```tsx
 * <EventRegisterPage />
 * ```
 */
export function EventRegisterPage() {
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header Section */}
        <div className="text-center mb-12 opacity-0 animate-fade-in-down" style={{ animationFillMode: 'forwards' }}>
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{
              background: 'linear-gradient(90deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
              border: '1px solid rgba(233, 69, 96, 0.3)',
            }}
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
              Registration Open
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
            <span className="text-gradient">Join the Reunion</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            Register now to secure your spot at the Re:Nav-96 30th Year Reunion.
            Reconnect with your 1996 batchmates and relive the memories.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - Event Info */}
          <div className="order-2 lg:order-1 opacity-0 animate-fade-in-left" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            {/* Countdown Timer */}
            <div className="mb-8">
              <h3 className="text-lg font-display font-semibold text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Countdown to Reunion
              </h3>
              <CountdownTimer />
            </div>

            {/* Event Details Card */}
            <div
              className="rounded-xl p-6 mb-8"
              style={{
                background: 'linear-gradient(135deg, rgba(45, 45, 74, 0.5) 0%, rgba(26, 26, 46, 0.5) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 className="text-lg font-display font-semibold text-white mb-4">Event Details</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="text-white font-medium">June 13th, 2026</p>
                    <p className="text-white/50 text-sm">Saturday</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-white font-medium">9:00 AM Onwards</p>
                    <p className="text-white/50 text-sm">Full day event</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="text-white font-medium">Venue TBA</p>
                    <p className="text-white/50 text-sm">Details coming soon</p>
                  </div>
                </div>
              </div>
            </div>

            {/* What You Get */}
            <div className="space-y-5">
              <h3 className="text-lg font-display font-semibold text-white mb-4">What You Get</h3>
              <Feature
                icon={
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                }
                title="Digital Ticket"
                description="Receive a unique QR-coded ticket for entry at the venue."
              />
              <Feature
                icon={
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                }
                title="Reunion Access"
                description="Join your batchmates for a day of nostalgia and celebration."
              />
              <Feature
                icon={
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
                title="Photo Access"
                description="Access and share memories in our exclusive photo gallery."
              />
              <Feature
                icon={
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                }
                title="Community Access"
                description="Stay connected with batchmates through our message boards."
              />
            </div>

            {/* Already Have Account Link */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-white/60 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:text-primary-light transition-colors">
                  Sign in here
                </Link>
              </p>
            </div>
          </div>

          {/* Right Column - Registration Form */}
          <div className="order-1 lg:order-2 opacity-0 animate-fade-in-right" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
            <div className="sticky top-24">
              <h2 className="text-2xl font-display font-bold text-white mb-6 text-center lg:text-left">
                Register Now
              </h2>
              <RegistrationForm />

              {/* Trust Indicators */}
              <div className="flex items-center justify-center gap-4 mt-6 text-xs text-white/40">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Secure
                </span>
                <span>•</span>
                <span>Quick Registration</span>
                <span>•</span>
                <span>Instant Confirmation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-12">
          <Link to="/" className="link text-sm">
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default EventRegisterPage;
